import type {RefObject} from 'react'
import {useEffect, useEffectEvent} from 'react'

import {isEditableElement} from '@github-ui/hotkey/keyboard-shortcuts-helper'

type OnKeyPressCallBack = (event: KeyboardEvent) => void

export const ModifierKeys = {
  ctrlKey: 'ctrlKey',
  altKey: 'altKey',
  shiftKey: 'shiftKey',
  metaKey: 'metaKey',
} as const

export type ModifierKeys = (typeof ModifierKeys)[keyof typeof ModifierKeys]

export type OnKeyPressOptions = {[key in ModifierKeys]?: boolean} & {
  triggerWhenInputElementHasFocus?: boolean
  triggerWhenPortalIsActive?: boolean
  scopeRef?: RefObject<HTMLElement | null>
  ignoreModifierKeys?: boolean
}

export const useKeyPress = (keys: string[], callback: OnKeyPressCallBack, options?: OnKeyPressOptions) => {
  const handleKeyPress = useEffectEvent((event: Event) => {
    const keyboardEvent = event as KeyboardEvent
    const currentKeys = keys
    const currentOptions = options
    // Check key match first — cheapest check, filters out the vast majority of events
    const keyMatches = currentKeys.some(key => {
      if (/^\d$/.test(key)) return keyboardEvent.code === `Digit${key}`
      if (key === '/') return keyboardEvent.code === 'Slash'
      return keyboardEvent.key === key
    })
    if (!keyMatches) return

    // Modifier keys — pure JS, near-zero cost
    if (!checkModifierKeys(keyboardEvent, currentOptions) && !currentOptions?.ignoreModifierKeys) {
      return
    }

    // Editable element check — single DOM property read
    if (!currentOptions?.triggerWhenInputElementHasFocus && isEditableElement(event.target)) {
      return
    }

    // Portal check last — most expensive, only runs when we'd actually fire. Two layers keep it cheap:
    // (1) per-event caching, so the document query + any layout reads run once per keydown no matter how many
    //     `useKeyPress` listeners are mounted; (2) `isPortalActive` skips the `clientHeight` layout read unless a
    //     portal root actually has content, so the common idle case is a no-reflow DOM-tree read only.
    if (!currentOptions?.triggerWhenPortalIsActive && isPortalActiveForEvent(event)) {
      return
    }

    callback(keyboardEvent)
  })

  const scopeRef = options?.scopeRef
  useEffect(() => {
    const targetElement = scopeRef?.current || document
    targetElement.addEventListener('keydown', handleKeyPress)
    return () => targetElement.removeEventListener('keydown', handleKeyPress)
  }, [scopeRef])
}

const portalSelectors = '#__primerPortalRoot__, [id$="-portal-root"]'

const activePortalCache = new WeakMap<Event, boolean>()

/**
 * A portal cannot appear or disappear during a single synchronous event dispatch, so we compute `isPortalActive` once
 * and cache it for the duration of that dispatch, keyed on the event in a WeakMap. This lets every `useKeyPress`
 * listener handling the same keydown reuse the first computed value instead of each repeating the document query and
 * layout reads. Keying on the event (rather than mutating it) avoids touching the browser-owned object; the entry is
 * released automatically once the event is garbage-collected.
 */
function isPortalActiveForEvent(event: Event): boolean {
  const cached = activePortalCache.get(event)
  if (cached !== undefined) return cached

  const active = isPortalActive()
  activePortalCache.set(event, active)
  return active
}

function isPortalActive() {
  const portals = document.querySelectorAll(portalSelectors)
  for (const portal of portals) {
    // `childElementCount` is a DOM-tree read and does NOT force layout. When no overlay is open the portal root
    // is empty, so this lets us skip `elementHasNonZeroHeight` (which reads `clientHeight` and can force a
    // synchronous reflow) entirely in the common idle case. We only measure height once a root actually has content.
    if (portal.childElementCount > 0 && elementHasNonZeroHeight(portal)) return true
  }
  return false
}

function elementHasNonZeroHeight(element: Element): boolean {
  if (element.clientHeight > 0) return true

  for (const child of element.children) {
    if (elementHasNonZeroHeight(child)) return true
  }

  return false
}

function checkModifierKeys(keyboardEvent: KeyboardEvent, options: OnKeyPressOptions | undefined) {
  for (const key of Object.values(ModifierKeys)) {
    if (options && options[key] && !keyboardEvent[key]) {
      // modifier key is required but not pressed
      return false
    }

    if (keyboardEvent[key] && (!options || !options[key])) {
      // modifier key is pressed but not required
      return false
    }
  }

  return true
}
